/*
 * Granu - conversão
 * 1) "+ adicionar" do "Leve também" no carrinho
 * 2) Barra de compra fixa na página de produto
 * 3) Calculadora de frete no carrinho
 * 4) Adicionais: "adicionado ✓" e foco no campo obrigatório
 * 5) Newsletter: cupom centralizado e em destaque
 * Usa os eventos do próprio Horizon para atualizar carrinho e preço.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

const raiz = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ---------- 1) Leve também ---------- */
document.addEventListener('click', async (evento) => {
  const botao = evento.target instanceof Element ? evento.target.closest('[data-granu-add]') : null;
  if (!botao || botao.disabled) return;
  evento.preventDefault();

  const textoOriginal = botao.textContent;
  botao.disabled = true;
  botao.textContent = botao.dataset.loading || '...';

  try {
    const resposta = await fetch(`${raiz}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(botao.dataset.granuAdd), quantity: 1 }] }),
    });
    const dados = await resposta.json();
    if (!resposta.ok) throw new Error(dados.description || dados.message || 'erro');

    const carrinho = await (await fetch(`${raiz}cart.js`, { headers: { Accept: 'application/json' } })).json();
    document.dispatchEvent(
      new CartAddEvent(carrinho, 'granu-leve-tambem', {
        source: 'granu-leve-tambem',
        itemCount: carrinho.item_count,
        variantId: String(botao.dataset.granuAdd),
      })
    );
  } catch (erro) {
    botao.textContent = botao.dataset.erro || 'Indisponível';
    setTimeout(() => {
      botao.textContent = textoOriginal;
      botao.disabled = false;
    }, 2500);
    return;
  }

  botao.textContent = textoOriginal;
  botao.disabled = false;
});

/* ---------- 2) Barra de compra fixa ---------- */
if (!customElements.get('granu-compra-fixa')) {
  customElements.define(
    'granu-compra-fixa',
    class extends HTMLElement {
      connectedCallback() {
        this.botaoPrincipal = this.encontrarBotaoPrincipal();
        if (!this.botaoPrincipal) return;

        this.removeAttribute('hidden');
        let agendado = false;
        this.verificar = () => {
          agendado = false;
          const principal = this.encontrarBotaoPrincipal();
          if (!principal) return this.classList.remove('is-visivel');
          const r = principal.getBoundingClientRect();
          this.classList.toggle('is-visivel', r.bottom < 0);
        };
        this.aoRolar = () => {
          if (agendado) return;
          agendado = true;
          requestAnimationFrame(this.verificar);
        };
        window.addEventListener('scroll', this.aoRolar, { passive: true });
        window.addEventListener('resize', this.aoRolar, { passive: true });
        this.verificar();

        this.querySelector('[data-granu-cf-botao]')?.addEventListener('click', () => {
          const alvo = this.encontrarBotaoPrincipal();
          if (!alvo) return;
          alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            if (!alvo.disabled) alvo.click();
          }, 450);
        });

        this.aoTrocarVariante = (evento) => {
          const html = evento?.detail?.data?.html;
          const precoNovo = html?.querySelector('product-price [ref="priceContainer"] .price, product-price [ref="priceContainer"]');
          const alvoPreco = this.querySelector('[data-granu-cf-preco]');
          if (precoNovo && alvoPreco) alvoPreco.textContent = precoNovo.textContent.trim().split(/\s{2,}/)[0];
          const botao = this.querySelector('[data-granu-cf-botao]');
          const principal = this.encontrarBotaoPrincipal();
          if (botao && principal) botao.disabled = principal.disabled;
        };
        document.addEventListener(ThemeEvents.variantUpdate, this.aoTrocarVariante);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.aoRolar);
        window.removeEventListener('resize', this.aoRolar);
        document.removeEventListener(ThemeEvents.variantUpdate, this.aoTrocarVariante);
      }

      encontrarBotaoPrincipal() {
        return document.querySelector(
          'main product-form-component button[name="add"], main product-form-component [ref="addToCartButton"]'
        );
      }
    }
  );
}

/* ---------- 3) Calculadora de frete no carrinho ---------- */
if (!customElements.get('granu-calc-frete')) {
  customElements.define(
    'granu-calc-frete',
    class extends HTMLElement {
      connectedCallback() {
        this.posicionar();
        this.observarZapiet();
        if (this.iniciado) return;
        this.iniciado = true;
        document.addEventListener('click', (e) => {
          if (e.target instanceof Element && e.target.closest('.checkoutMethod')) {
            setTimeout(() => this.atualizarVisibilidade(), 60);
            setTimeout(() => this.atualizarVisibilidade(), 400);
          }
        });
        this.form = this.querySelector('form');
        this.input = this.querySelector('input');
        this.botao = this.querySelector('button');
        this.saida = this.querySelector('.granu-calc__resultado');
        try {
          const salvo = sessionStorage.getItem('granu-cep');
          if (salvo) this.input.value = salvo;
        } catch (e) {}
        this.input.addEventListener('input', () => {
          const d = this.input.value.replace(/\D/g, '').slice(0, 8);
          this.input.value = d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
        });
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.calcular();
        });
      }

      disconnectedCallback() {
        if (this.obs) this.obs.disconnect();
      }

      /* Fica logo abaixo das opções do Zapiet (retirada / motoboy / correios) */
      posicionar() {
        const zap = (this.closest('cart-drawer-component') || document).querySelector('#storePickupApp');
        if (zap && zap.nextElementSibling !== this) zap.after(this);
      }

      /* Só aparece com "Envio via correios" selecionado; motoboy usa o CEP do próprio Zapiet */
      atualizarVisibilidade() {
        const escopo = this.closest('cart-drawer-component') || document;
        const metodos = escopo.querySelectorAll('.checkoutMethod');
        if (!metodos.length) {
          /* Zapiet ainda carregando no #storePickupApp: esconde para não piscar antes de saber o método */
          this.hidden = !!(document.getElementById('storePickupApp') && window.ZapietCachedSettings);
          return;
        }
        const ativo = escopo.querySelector('.checkoutMethod.active');
        this.hidden = !(ativo && /correio/i.test(ativo.textContent));
      }

      observarZapiet() {
        if (this.obs) this.obs.disconnect();
        const alvo = this.closest('cart-drawer-component') || document.body;
        this.obs = new MutationObserver(() => this.atualizarVisibilidade());
        this.obs.observe(alvo, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
        this.atualizarVisibilidade();
      }

      mensagem(texto) {
        this.saida.innerHTML = '';
        const p = document.createElement('p');
        p.className = 'granu-calc__msg';
        p.textContent = texto;
        this.saida.appendChild(p);
      }

      async calcular() {
        const cep = this.input.value.replace(/\D/g, '');
        if (cep.length !== 8) return this.mensagem('Digite um CEP com 8 números.');
        try { sessionStorage.setItem('granu-cep', this.input.value); } catch (e) {}
        this.botao.disabled = true;
        this.mensagem('Calculando...');
        try {
          let uf = '';
          try {
            const via = await (await fetch(`https://viacep.com.br/ws/${cep}/json/`)).json();
            if (via && !via.erro) uf = via.uf || '';
          } catch (e) {}
          if (!uf) throw new Error('CEP não encontrado.');

          const params = new URLSearchParams({
            'shipping_address[zip]': this.input.value,
            'shipping_address[country]': 'Brazil',
            'shipping_address[province]': uf,
          });
          const prep = await fetch(`${raiz}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' });
          if (!prep.ok) throw new Error('Não foi possível calcular para este CEP.');

          let taxas = null;
          for (let i = 0; i < 12 && !taxas; i++) {
            const r = await fetch(`${raiz}cart/async_shipping_rates.json?${params}`);
            if (r.status === 200) {
              const dados = await r.json();
              taxas = dados.shipping_rates || [];
            } else {
              await new Promise((ok) => setTimeout(ok, 700));
            }
          }
          if (!taxas) throw new Error('O cálculo demorou demais. Tente de novo.');
          if (!taxas.length) return this.mensagem('Sem opções de entrega para este CEP.');

          const fmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
          const ul = document.createElement('ul');
          taxas
            .slice()
            .sort((a, b) => Number(a.price) - Number(b.price))
            .forEach((t) => {
              const li = document.createElement('li');
              const nome = document.createElement('span');
              let prazo = '';
              if (t.delivery_days && t.delivery_days.length) {
                const [min, max] = t.delivery_days;
                prazo = max && max !== min ? ` (${min} a ${max} dias úteis)` : ` (${min} dia${min > 1 ? 's úteis' : ' útil'})`;
              }
              nome.textContent = `${t.name}${prazo}`;
              const preco = document.createElement('span');
              preco.className = 'granu-calc__preco';
              preco.textContent = Number(t.price) === 0 ? 'Grátis' : fmt.format(Number(t.price));
              li.append(nome, preco);
              ul.appendChild(li);
            });
          this.saida.innerHTML = '';
          this.saida.appendChild(ul);
        } catch (erro) {
          this.mensagem(erro.message || 'Não foi possível calcular agora.');
        } finally {
          this.botao.disabled = false;
        }
      }
    }
  );
}

/* ---------- 4) Adicionais: botão "adicionado ✓" e foco no campo obrigatório ---------- */
(() => {
  const sincronizar = (card) => {
    const botao = card.querySelector('.suggested-variant-card__add');
    if (!botao) return;
    if (!botao.dataset.granuTexto) botao.dataset.granuTexto = botao.textContent.trim();
    const adicionado = card.classList.contains('suggested-variant--added');
    botao.textContent = adicionado ? 'adicionado ✓' : botao.dataset.granuTexto;
    botao.classList.toggle('granu-sv-ok', adicionado);
    botao.setAttribute('aria-pressed', adicionado ? 'true' : 'false');
  };
  const observador = new MutationObserver((mudancas) => {
    mudancas.forEach((m) => {
      if (m.target instanceof Element && m.target.classList.contains('suggested-variant-card')) sincronizar(m.target);
    });
  });
  const iniciar = () => {
    document.querySelectorAll('.suggested-variant-card:not([data-granu-sv])').forEach((card) => {
      card.dataset.granuSv = '1';
      sincronizar(card);
      observador.observe(card, { attributes: true, attributeFilter: ['class'] });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
  document.addEventListener(ThemeEvents.variantUpdate, () => setTimeout(iniciar, 300));

  document.addEventListener('click', (evento) => {
    const botao = evento.target instanceof Element ? evento.target.closest('.suggested-variant-card__add') : null;
    if (!botao || botao.classList.contains('granu-sv-ok')) return;
    const card = botao.closest('.suggested-variant-card');
    const variante = card && card.dataset.variantId;
    setTimeout(() => {
      iniciar();
      const bloco = botao.closest('.suggested-variants-block') || document;
      const item = variante ? bloco.querySelector(`[data-summary-item-for="${variante}"]`) : null;
      if (!item) return;
      const campo = [...item.querySelectorAll('input, textarea')].find(
        (c) => c.required && c.type !== 'hidden' && !c.value
      );
      if (!campo) return;
      campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => campo.focus({ preventScroll: true }), 500);
    }, 350);
  });
})();

/* ---------- 5) Newsletter (Shopify Forms): cupom centralizado e em destaque ---------- */
(() => {
  const ajustar = (raiz) => {
    const codigos = [...raiz.querySelectorAll('*')].filter(
      (e) => e.childElementCount === 0 && /^[A-Z0-9]{5,20}$/.test(e.textContent.trim())
    );
    codigos.forEach((codigo) => {
      if (codigo.dataset.granuCupom) return;
      codigo.dataset.granuCupom = '1';
      const bloco = codigo.closest('section') || codigo.parentElement.parentElement || codigo.parentElement;
      const cor = getComputedStyle(bloco).color;
      bloco.style.textAlign = 'center';
      [bloco, ...bloco.querySelectorAll('*')].forEach((e) => {
        const cs = getComputedStyle(e);
        if (cs.display.includes('flex')) { e.style.justifyContent = 'center'; e.style.alignItems = 'center'; }
      });
      const caixa = codigo.parentElement;
      Object.assign(caixa.style, {
        display: 'inline-flex', alignItems: 'center', gap: '10px',
        border: '1.5px dashed ' + cor, borderRadius: '10px',
        padding: '10px 18px', margin: '12px auto', color: cor, background: 'transparent',
      });
      Object.assign(codigo.style, { color: cor, fontSize: '28px', letterSpacing: '2px', fontWeight: '700' });
      caixa.querySelectorAll('svg, svg *').forEach((s) => { s.style.fill = cor; s.style.color = cor; });
    });
  };
  const ligar = () => {
    document.querySelectorAll('shopify-forms-embed').forEach((embed) => {
      if (!embed.shadowRoot || embed.dataset.granuObs) return;
      embed.dataset.granuObs = '1';
      new MutationObserver(() => ajustar(embed.shadowRoot)).observe(embed.shadowRoot, { childList: true, subtree: true });
      ajustar(embed.shadowRoot);
    });
  };
  ligar();
  setTimeout(ligar, 2000);
  setTimeout(ligar, 6000); document.addEventListener('granu:forms-liberado', () => { [1500, 4000, 8000].forEach((ms) => setTimeout(ligar, ms)); });
})();

/* 6. Zapiet na gaveta: atualiza produtos/colecoes quando o carrinho muda sem recarregar.
   O Zapiet monta a lista de produtos (com colecoes) so no carregamento da pagina; sem isso,
   produto adicionado pela gaveta cai como "nao disponivel" para Envio via Correios. */
(() => {
  let rodando = false;
  let pendente = false;
  const chave = (lista) => (lista || []).map((p) => p.variant_id).sort().join(',');
  const sincronizar = async () => {
    if (rodando) { pendente = true; return; }
    rodando = true;
    try {
      const s = window.ZapietCachedSettings;
      if (!s || !window.Zapiet || typeof window.Zapiet.start !== 'function') return;
      const html = await fetch('/?section_id=granu-zapiet-dados', { credentials: 'same-origin' }).then((r) => r.text());
      const m = html.match(/id="granu-zapiet-dados"[^>]*>([\s\S]*?)<\/script>/);
      if (!m) return;
      const novos = JSON.parse(m[1]);
      if (chave(novos) === chave(s.products)) return;
      s.products = novos;
      const el = document.getElementById('storePickupApp');
      if (el && novos.length) {
        el.innerHTML = '';
        window.Zapiet.start(s);
      }
    } catch (e) {
      /* silencioso */
    } finally {
      rodando = false;
      if (pendente) { pendente = false; sincronizar(); }
    }
  };
  document.addEventListener('cart:update', () => setTimeout(sincronizar, 300));
})();

/* 7. Monte sua Cesta: regua de itens (min/max lidos de data-min-items / data-max-items da secao) */
(() => {
  const plural = (k) => k + (k === 1 ? ' item' : ' itens');
  const iniciar = (sec) => {
    if (sec.dataset.granuRegua) return;
    sec.dataset.granuRegua = '1';
    const min = parseInt(sec.dataset.minItems || '10', 10);
    const max = parseInt(sec.dataset.maxItems || '15', 10);
    const criar = (extra) => {
      const el = document.createElement('div');
      el.className = 'granu-regua ' + extra;
      el.innerHTML = '<p class="granu-regua__texto" aria-live="polite"></p><div class="granu-regua__trilho" role="progressbar" aria-valuemin="0" aria-valuemax="' + min + '"><div class="granu-regua__barra"></div></div>';
      return el;
    };
    const reguas = [];
    const desk = sec.querySelector('.monte-kit__summary-header');
    if (desk) { const r = criar('granu-regua--desk'); desk.insertAdjacentElement('afterend', r); reguas.push(r); }
    const mob = sec.querySelector('.monte-kit__summary-mobile');
    if (mob) { const r = criar('granu-regua--mob'); mob.prepend(r); reguas.push(r); }
    const botoes = [...sec.querySelectorAll('.monte-kit__add-to-cart, .monte-kit__add-to-cart-mobile, .monte-kit__drawer-add-cart')];
    botoes.forEach((b) => { b.dataset.granuTexto = b.textContent.trim(); });
    const contar = () => {
      const c = sec.querySelector('.monte-kit__selected-count');
      let n = c ? parseInt(c.textContent, 10) : NaN;
      if (isNaN(n)) n = [...sec.querySelectorAll('.monte-kit__qty-input')].reduce((a, i) => a + (parseInt(i.value, 10) || 0), 0);
      return n;
    };
    const ajustarBotoes = (n) => {
      const falta = min - n;
      botoes.forEach((b) => {
        const atual = b.textContent.trim();
        if (falta > 0) {
          const t = 'Adicione mais ' + plural(falta);
          if (atual !== t && !/adicionando|carregando/i.test(atual)) b.textContent = t;
        } else if (atual.startsWith('Adicione mais')) {
          b.textContent = b.dataset.granuTexto;
        }
      });
    };
    let ultimo = -1;
    const atualizar = () => {
      const n = contar();
      if (n !== ultimo) {
        ultimo = n;
        const falta = Math.max(0, min - n);
        let txt; let estado;
        if (n >= max) { txt = 'Limite de ' + max + ' itens atingido ✓'; estado = 'max'; }
        else if (n >= min) { txt = 'Cesta completa ✓ Você ainda pode adicionar até ' + plural(max - n); estado = 'ok'; }
        else if (n === 0) { txt = 'Escolha no mínimo ' + min + ' itens para montar sua cesta'; estado = 'vazio'; }
        else { txt = n + ' de ' + min + ' — ' + (falta === 1 ? 'falta ' : 'faltam ') + plural(falta) + ' para completar sua cesta'; estado = 'falta'; }
        reguas.forEach((r) => {
          r.dataset.estado = estado;
          r.querySelector('.granu-regua__texto').textContent = txt;
          r.querySelector('.granu-regua__barra').style.width = Math.min(100, (n / min) * 100) + '%';
          r.querySelector('.granu-regua__trilho').setAttribute('aria-valuenow', String(Math.min(n, min)));
        });
      }
      ajustarBotoes(n);
    };
    let agendado = null;
    const agendar = () => {
      if (agendado) return;
      agendado = setTimeout(() => { agendado = null; atualizar(); }, 40);
    };
    new MutationObserver(agendar).observe(sec, { subtree: true, childList: true, characterData: true });
    ['click', 'input', 'change'].forEach((ev) => sec.addEventListener(ev, agendar));
    atualizar();
  };
  const ligar = () => document.querySelectorAll('.monte-kit-section[data-min-items]').forEach(iniciar);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ligar); else ligar();
})();

/* 9. Zapiet na gaveta: mantem a modalidade e o CEP escolhidos quando o carrinho muda (ex.: quantidade). */
(() => {
  const CHAVE = 'granuZapiet';
  const ler = () => { try { return JSON.parse(sessionStorage.getItem(CHAVE) || 'null'); } catch (e) { return null; } };
  const gravar = (v) => { try { sessionStorage.setItem(CHAVE, JSON.stringify(v)); } catch (e) { /* sem storage */ } };
  const zap = () => (window.Zapiet && typeof window.Zapiet.getSelectedCheckoutMethod === 'function' ? window.Zapiet : null);
  let tentativas = null;
  const salvar = () => {
    const z = zap();
    if (!z) return;
    const metodo = z.getSelectedCheckoutMethod();
    if (!metodo) return;
    const campo = document.querySelector('#storePickupApp #deliveryGeoSearchField');
    const anterior = ler() || {};
    gravar({ metodo, cep: campo && campo.value ? campo.value : (metodo === anterior.metodo ? anterior.cep || '' : '') });
  };
  const doZapiet = (ev) => ev.target && ev.target.closest && ev.target.closest('#storePickupApp');
  document.addEventListener('click', (ev) => { if (doZapiet(ev)) { clearInterval(tentativas); setTimeout(salvar, 600); } }, true);
  document.addEventListener('change', (ev) => { if (doZapiet(ev)) setTimeout(salvar, 300); }, true);
  const restaurar = () => {
    const s = ler();
    const z = zap();
    if (!s || !z || !document.querySelector('#storePickupApp .checkoutMethod')) return false;
    try {
      if (z.getSelectedCheckoutMethod() !== s.metodo) z.setCheckoutMethod(s.metodo);
      const campo = document.querySelector('#storePickupApp #deliveryGeoSearchField');
      if (s.metodo === 'delivery' && s.cep && campo && !campo.value) z.setDeliveryPostcode(s.cep);
    } catch (e) { return false; }
    return z.getSelectedCheckoutMethod() === s.metodo;
  };
  document.addEventListener('cart:update', () => {
    clearInterval(tentativas);
    let n = 0;
    tentativas = setInterval(() => {
      n += 1;
      const ok = restaurar();
      if ((ok && n >= 8) || n >= 20) clearInterval(tentativas);
    }, 300);
  });
})();
